import { Archive, ArchiveRestore, ExternalLink, History, MoreHorizontal } from 'lucide-react'
import { Link } from 'react-router-dom'

import { StatusPill } from '../../../components/primitives/StatusPill'
import type { Status } from '../../../components/primitives/types'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu'
import { formatRelativeTime } from '../../../lib/time'
import { cn } from '../../../lib/utils'
import {
  tabularConfigBuildPath,
  tabularConfigPath,
  tabularJobPath
} from '../../TabularIngestJobPage/route'
import { intakesTabPath } from '../intakes/intake-view'
import { statusToLabel, statusToPill } from '../util/jobStatusMap'
import {
  configsTabPath,
  type BuildCell,
  type ConfigRow,
  type LastRunCell,
  type UsedByEntry
} from './config-view'

const STATUS_TEXT: Record<Status, string> = {
  run: 'text-st-run',
  warn: 'text-st-warn',
  down: 'text-st-down',
  idle: 'text-ink-2',
  info: 'text-st-info'
}

export interface ConfigsTableRow {
  config: ConfigRow
  /** Absent when the builds could not be loaded. */
  build?: BuildCell
  /** Absent when the intakes could not be loaded. */
  usedBy?: UsedByEntry[]
  lastRun?: LastRunCell
}

const COLUMN_COUNT = 7

interface ConfigsTableProps {
  /** Active configs, listed first. */
  rows: ConfigsTableRow[]
  /** Archived configs, listed in their own section below the active ones; empty to hide it. */
  archivedRows?: ConfigsTableRow[]
  /** Intake names link to the Intakes tab only for users who can see it. */
  linkIntakes: boolean
  onArchive: (config: ConfigRow) => void
  onUnarchive: (config: ConfigRow) => void
}

export function ConfigsTable({
  rows,
  archivedRows = [],
  linkIntakes,
  onArchive,
  onUnarchive
}: ConfigsTableProps) {
  const from = configsTabPath()
  const renderRow = (row: ConfigsTableRow) => (
    <ConfigRowView
      key={row.config.name}
      row={row}
      from={from}
      linkIntakes={linkIntakes}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
    />
  )
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed text-left text-sm">
          <thead className="border-b border-line text-ink-3">
            <tr>
              <th className="px-4 py-2 text-xs font-medium uppercase tracking-wider">Name</th>
              <th className="w-32.5 py-2 pr-3 text-xs font-medium uppercase tracking-wider">
                Version
              </th>
              <th className="w-16 py-2 pr-3 text-xs font-medium uppercase tracking-wider">
                Tables
              </th>
              <th className="w-45 py-2 pr-3 text-xs font-medium uppercase tracking-wider">Build</th>
              <th className="w-37.5 py-2 pr-3 text-xs font-medium uppercase tracking-wider">
                Used by
              </th>
              <th className="w-22.5 py-2 pr-3 text-xs font-medium uppercase tracking-wider">
                Last run
              </th>
              <th className="w-13 py-2 pr-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && archivedRows.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="px-4 py-6 text-center text-sm text-ink-3">
                  No active tabular configs. Choose All to see archived ones.
                </td>
              </tr>
            ) : null}
            {rows.map(renderRow)}
            {archivedRows.length > 0 ? (
              <tr className="border-b border-line bg-bg-sunk">
                <th
                  scope="rowgroup"
                  colSpan={COLUMN_COUNT}
                  className="px-4 py-1.5 text-11 font-medium tracking-[0.08em] text-ink-3 uppercase"
                >
                  Archived ({archivedRows.length})
                </th>
              </tr>
            ) : null}
            {archivedRows.map(renderRow)}
          </tbody>
        </table>
      </div>
      <div className="border-t border-line px-4 py-2 text-xs text-ink-3">
        {rows.length} tabular config{rows.length === 1 ? '' : 's'}
        {archivedRows.length > 0 ? `, ${archivedRows.length} archived` : ''}. Open one for its
        versions, checks, and history.
      </div>
    </Card>
  )
}

function ConfigRowView({
  row: { config, build, usedBy, lastRun },
  from,
  linkIntakes,
  onArchive,
  onUnarchive
}: {
  row: ConfigsTableRow
  from: string
  linkIntakes: boolean
  onArchive: (config: ConfigRow) => void
  onUnarchive: (config: ConfigRow) => void
}) {
  const description = config.latest.definition.description?.split('\n')[0]?.trim()
  return (
    <tr className="border-b border-line last:border-b-0">
      <td className="overflow-hidden px-4 py-2">
        <Link
          to={tabularConfigPath(config.name)}
          state={{ from }}
          title={config.name}
          className={cn(
            'block truncate font-mono text-xs hover:underline',
            config.archived ? 'text-ink-3' : 'text-ink'
          )}
        >
          {config.name}
        </Link>
        {description ? (
          <div className="truncate text-11 text-ink-3" title={description}>
            {description}
          </div>
        ) : null}
      </td>
      <td className="py-2 pr-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={cn('font-mono text-xs', config.archived ? 'text-ink-3' : 'text-ink')}>
            v{config.latest.version}
          </span>
          <span className="rounded border border-line bg-bg-elev px-1.5 py-px font-mono text-10 text-ink-2">
            {config.parserKind}
          </span>
        </div>
      </td>
      <td
        className={cn('py-2 pr-3 text-xs', config.archived ? 'text-ink-3' : 'text-ink-2')}
        title={config.tableNames.join(', ')}
      >
        {config.tableNames.length}
      </td>
      <td className="overflow-hidden py-2 pr-3 text-xs">
        <BuildCellView name={config.name} build={build} from={from} />
      </td>
      <td className="overflow-hidden py-2 pr-3 text-xs">
        <UsedByCellView usedBy={usedBy} linkIntakes={linkIntakes} />
      </td>
      <td className="py-2 pr-3 text-xs">
        <LastRunCellView lastRun={lastRun} from={from} />
      </td>
      <td className="py-2 pr-3 text-right">
        <div className="inline-flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                aria-label={`Actions for ${config.name}`}
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem asChild>
                <Link to={tabularConfigPath(config.name)} state={{ from }}>
                  <ExternalLink />
                  Open
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={tabularConfigPath(config.name, { tab: 'history' })} state={{ from }}>
                  <History />
                  History
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {config.archived ? (
                <DropdownMenuItem onSelect={() => onUnarchive(config)}>
                  <ArchiveRestore />
                  Unarchive…
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem variant="destructive" onSelect={() => onArchive(config)}>
                  <Archive />
                  Archive…
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  )
}

function BuildCellView({
  name,
  build,
  from
}: {
  name: string
  build: BuildCell | undefined
  from: string
}) {
  if (!build || build.kind === 'none') return <span className="text-ink-3">—</span>
  const pill =
    build.kind === 'building' ? (
      <StatusPill status="info">{build.mode === 'update' ? 'updating' : 'building'}</StatusPill>
    ) : (
      <StatusPill status="warn">
        {build.proposedVersion !== undefined
          ? `draft v${build.proposedVersion} awaiting review`
          : 'draft awaiting review'}
      </StatusPill>
    )
  return (
    <Link
      to={tabularConfigBuildPath(name, build.jobId)}
      state={{ from }}
      className="inline-flex max-w-full hover:underline"
    >
      {pill}
    </Link>
  )
}

function UsedByCellView({
  usedBy,
  linkIntakes
}: {
  usedBy: UsedByEntry[] | undefined
  linkIntakes: boolean
}) {
  if (!usedBy) return <span className="text-ink-3">—</span>
  if (usedBy.length === 0) return <span className="text-ink-3">no intake</span>
  return (
    <div className="flex min-w-0 flex-col">
      {usedBy.map((entry) => {
        const label = `${entry.intakeName}${entry.pinnedVersion ? ` @v${entry.pinnedVersion}` : ''}`
        const title = entry.pinnedVersion
          ? `${entry.intakeName} runs v${entry.pinnedVersion}`
          : `${entry.intakeName} runs the latest version`
        return linkIntakes ? (
          <Link
            key={entry.intakeId}
            to={intakesTabPath(entry.intakeId)}
            title={title}
            className="truncate font-mono text-11 text-st-info hover:underline"
          >
            {label}
          </Link>
        ) : (
          <span
            key={entry.intakeId}
            title={title}
            className="truncate font-mono text-11 text-ink-2"
          >
            {label}
          </span>
        )
      })}
    </div>
  )
}

function LastRunCellView({ lastRun, from }: { lastRun: LastRunCell | undefined; from: string }) {
  if (!lastRun) return <span className="text-ink-3">—</span>
  const status = statusToPill[lastRun.state]
  return (
    <Link
      to={tabularJobPath(lastRun.jobId)}
      state={{ from }}
      title={statusToLabel[lastRun.state]}
      className={cn('hover:underline', STATUS_TEXT[status])}
    >
      {formatRelativeTime(lastRun.submittedAt, { fallback: '—' })}
    </Link>
  )
}
